import {useDeepMemo} from '@wandb/weave/hookUtils';
import {useEffect, useMemo, useReducer} from 'react';

import {
  type RunInfoFragmentFragment,
  useRunsInfoQuery,
} from '../../../generated/graphql';
import {logError} from '../../../services/errors/errorReporting';
import {runNamesFilter, toMongo} from '../../../util/filters';
import {type Query, type RunSetQuery} from '../../../util/queryTypes';
import {useFullFidelityModeLineType} from '../../../util/rampFeatureFlags/linePlotDynamicConfigs';
import {
  useEnableBackendGrouping,
  useRampFlagUseBackendSmoothing,
  useStatsigGateColumnarBucketed,
} from '../../../util/rampFeatureFlags/linePlotFeatureFlags';
import {groupingToServerGroupKeys} from '../../../util/runs';
import {useStablePanelId} from '../../Panel/PanelIDContext';
import {useSharedPanelState} from '../../Panel/SharedPanelStateContext';
import {POINT_VISUALIZATION_OPTIONS} from '../../WorkspaceDrawer/Settings/types';
import {type Range, runsLinePlotTransformQuery} from '../common';
import {useBackendLineConstruction} from '../componentOutliers/useBackendLineConstruction';
import {usePanelConfigContext} from '../PanelConfigContext';
import {type QueryStoreRequest} from '../QueryStore/queryStore';
import {usePanelGroupingSettings} from '../RunsLinePlotContext/usePanelGroupingSettings';
import {usePanelSystemMetricsContext} from '../SystemMetrics/PanelSystemMetricsContext';
import {type BucketedLine, type RunsLinePlotConfig} from '../types';
import {bucketedRunsReducer} from './bucketedDataReducer';
import {type BucketedQueryState} from './bucketedQueryManager';
import {getOrderedRuns} from './getOrderedRuns';
import {
  checkBackendLinesSupport,
  checkColumnarBucketedSupport,
} from './queries/checks';
import {getQueryType} from './queries/getQueryType';
import {useLoggingInfo} from './useLoggingInfo';
import {bracketHistoriesData, mapEnabledByRunSets} from './util';

const usePanelQuery = (
  pageQuery: Query,
  config: RunsLinePlotConfig,
  queryZoomRange: Range,
  skip?: boolean
) => {
  const {
    fullFidelityMode,
    groupedFullFidelityMode,
    groupingDepth,
    limit: maxRunsOrGroups,
    parsedExpressions,
    stabilizeRunGroups,
  } = usePanelConfigContext();
  const enableBackendSmoothing = useRampFlagUseBackendSmoothing();
  const {isAnyRunsetGrouped} = usePanelGroupingSettings();
  const primaryLineType = useFullFidelityModeLineType({
    fullFidelityMode,
    groupedFullFidelityMode,
    isGrouped: isAnyRunsetGrouped,
  });
  const {isDelayedSystemMetrics} = usePanelSystemMetricsContext();

  const transformed =
    skip || isDelayedSystemMetrics
      ? null
      : runsLinePlotTransformQuery({
          enableBackendSmoothing,
          primaryLineType,
          isFullFidelity: true,
          maxRunsOrGroups,
          parsedExpressions,
          query: pageQuery,
          runsLinePlotConfig: {...config, groupingDepth, stabilizeRunGroups},
          // not using this yet, coming in a fast follow
          // useCappedSystemMetrics:
          //   shouldCapSystemMetrics && isSystemMetricsPanel,
          xStepRange: queryZoomRange,
        });

  return useDeepMemo(transformed);
};

export function useBucketedData({
  config,
  isInReport,
  pageQuery,
  queryZoomRange,
  nBuckets,
  skip,
}: {
  config: RunsLinePlotConfig;
  isInReport?: boolean;
  pageQuery: Query;
  queryZoomRange: Range;
  nBuckets: number;
  skip?: boolean;
}) {
  const {aggregatePanelMetrics, isAnyRunsetGrouped, getGroupKeysByRunsetId} =
    usePanelGroupingSettings();
  const panelQuery = usePanelQuery(pageQuery, config, queryZoomRange, skip);
  const {bucketQueryManagerById, queryStore} = useSharedPanelState();

  const groupKeys = useMemo(() => {
    return pageQuery?.runSets != null
      ? pageQuery.runSets.flatMap(runSet => {
          return getGroupKeysByRunsetId(runSet.id);
        })
      : [];
    // oxlint-disable-next-line react/preserve-manual-memoization
  }, [pageQuery?.runSets, getGroupKeysByRunsetId]);
  const groupKeyNames = useMemo(() => {
    return groupingToServerGroupKeys(groupKeys, 'useBucketedData');
  }, [groupKeys]);

  const [
    {
      aggregatedByRunsetId,
      allRunsetsHaveBackendLines,
      backendLinesDomain,
      fetchedRunCount,
      fetchedRunCountByRunsetId,
      hasSubsampledGroups,
      isLoading,
      runDataByRunsetId,
      runNames,
      totalGroupedIncludedRuns,
    },
    bucketedRunsDispatch,
  ] = useReducer(bucketedRunsReducer, {
    aggregatedByRunsetId: {},
    allRunsetsHaveBackendLines: false,
    backendLinesDomain: null,
    fetchedRunCount: 0,
    fetchedRunCountByRunsetId: {},
    hasSubsampledGroups: false,
    loadingByRunSetId: {},
    isLoading: true, // initially set to true so that we don't flash an error on screen for missing data when not loading
    runDataByRunsetId: {},
    runNames: [],
    totalGroupedIncludedRuns: 0,
  });

  const {
    canUseBackendLineConstruction,
    canUseBackendLineConstructionGrouped,
    canUseBackendLineConstructionInReports,
    canUseBackendLineConstructionSmoothing,
  } = useBackendLineConstruction();
  const enableBackendGrouping = useEnableBackendGrouping();
  // Gate on the ramp flag and panel support.
  const allowsColumnarBucketed =
    useStatsigGateColumnarBucketed() && checkColumnarBucketedSupport(config);

  const loggingInfo = useLoggingInfo();

  const {deltaQueryTypeOverride} = usePanelConfigContext();

  const panelQueryWithGroupKeys = useMemo(() => {
    if (!panelQuery) {
      return null;
    }
    return {
      ...panelQuery,
      groupKeys: groupKeyNames,
      isGrouped: isAnyRunsetGrouped,
      // Clear baseline/pinned IDs if feature flags are disabled
      queries: panelQuery.runsetQueries.map(q => ({
        ...q,
        baselineRunId: q.baselineRunId,
        pinnedRunIds: q.pinnedRunIds,
      })),
    };
  }, [panelQuery, groupKeyNames, isAnyRunsetGrouped]);

  const panelId = useStablePanelId();

  const supportsBackendLineConstruction = checkBackendLinesSupport({
    config,
    context: {
      isInReport,
      panelId,
      usesBackendLineConstruction: canUseBackendLineConstruction,
      usesBackendLineConstructionInReports:
        canUseBackendLineConstructionInReports,
    },
    usesBackendLineConstructionSmoothing:
      canUseBackendLineConstructionSmoothing,
  });
  const allowsBackendLineConstruction =
    supportsBackendLineConstruction && canUseBackendLineConstruction;
  const allowsGroupedBackendLineConstruction =
    allowsBackendLineConstruction && canUseBackendLineConstructionGrouped;

  useEffect(() => {
    if (!panelQueryWithGroupKeys || !pageQuery.runSets?.length) {
      // If there are no runSets, there should also be no queries in panelQuery.queries. Sometimes, due to behavior in
      // toRunsDataQuery in lib.ts, there will be an item in panelQuery.queries but no runSets. This is because it puts
      // the pageQuery in queries instead of creating a panel query in this case. Either way, if there are no runSets,
      // there is no need to do any querying here so we can eject.
      return;
    }
    const managers = bucketQueryManagerById.current;
    // One per query: a store release, or a manager unregister.
    const cleanups: (() => void)[] = [];

    panelQueryWithGroupKeys.queries.forEach(q => {
      const usesInternalID = !!q.internalProjectId;

      const queryType = getQueryType({
        aggregateMetrics: aggregatePanelMetrics,
        allowsBackendLineConstruction,
        allowsColumnarBucketed,
        allowsGroupedBackendLineConstruction,
        enableBackendGrouping,
        historyKeys:
          panelQueryWithGroupKeys.bucketedHistorySpecs?.flatMap(
            spec => spec.keys
          ) ?? [],
        isGrouped: isAnyRunsetGrouped,
        runSets: pageQuery?.runSets ?? [],
        usesInternalId: usesInternalID,
        deltaQueryTypeOverride,
        supportsBackendLineConstruction,
      });

      const handlerDataChange = (state: BucketedQueryState) => {
        const bracketedState = bracketHistoriesData(state, config.xAxis);

        bucketedRunsDispatch({
          type: 'bucketedRuns/combo',
          data: bracketedState,
          isAggregated: !!state.data?.isAggregated,
          loading: state.loading,
          runsetId: q.id,
        });
      };

      const request: QueryStoreRequest = {
        expressions: config.expressions,
        loggingInfo,
        nBuckets,
        queryType,
        runsDataQuery: panelQueryWithGroupKeys,
        singleQuery: q,
      };

      if (queryStore) {
        cleanups.push(queryStore.subscribe(request, handlerDataChange));
        return;
      }
      cleanups.push(() => managers[q.id]?.unregisterRequest(handlerDataChange));

      const matchedQueryManager = bucketQueryManagerById.current[q.id];
      if (matchedQueryManager) {
        matchedQueryManager.registerRequest({
          ...request,
          handler: handlerDataChange,
        });
      } else {
        const {queries, ...pQuery} = panelQueryWithGroupKeys;

        const error = new ReferenceError(
          'Full fidelity error: no bucketed query manager found for query'
        );
        const context = {
          bucketedQueryManager: Object.keys(
            bucketQueryManagerById.current
          ).join(', '),
          panelQuery: pQuery,
          queries: q,
        };
        // @ts-ignore the reporter can accept key/value pairs
        logError(error, context);
      }
    });

    return () => cleanups.forEach(cleanup => cleanup());
  }, [
    aggregatePanelMetrics,
    allowsBackendLineConstruction,
    allowsColumnarBucketed,
    allowsGroupedBackendLineConstruction,
    bucketQueryManagerById,
    canUseBackendLineConstruction,
    config.expressions,
    config.xAxis,
    deltaQueryTypeOverride,
    enableBackendGrouping,
    groupKeyNames,
    isAnyRunsetGrouped,
    loggingInfo,
    nBuckets,
    pageQuery?.runSets,
    panelQueryWithGroupKeys,
    queryStore,
    supportsBackendLineConstruction,
  ]);

  const {
    data: runsInfoData,
    loading: runsInfoLoading,
    error: runsInfoError,
  } = useRunsInfoQuery({
    variables: {
      projectName: pageQuery.projectName,
      entityName: pageQuery.entityName,
      filters: JSON.stringify(toMongo(runNamesFilter(runNames))),
    },
    // NOTE: useRunsInfoQuery is scoped to the workspace's project, so cross-project runs
    // will never resolve and can stick the query in loading=true indefinitely.
    // So, for now, we skip runInfo query entirely.
    //
    // The downsides: minimal. HOWEVER, it could break a user's flow if they wanted to
    // use gpu/gpuCount for run grouping, semantic line coloring, in chart legends.
    // TODO: update runsInfoQuery to support cross-project lookups
    // (by ID rather than name+project), which would let us drop this skip.
    skip: runNames.length === 0 || true,
  });

  // Memoize enabled keys to prevent unnecessary recalculations
  const enabledKeys = useMemo(() => {
    const runSets = mapEnabledByRunSets(pageQuery.runSets ?? []);
    return Object.keys(runDataByRunsetId).filter(key => runSets[key] ?? false);
  }, [runDataByRunsetId, pageQuery.runSets]);

  const runInfoMap: Partial<
    Record<string, RunInfoFragmentFragment['runInfo']>
  > = useMemo(() => {
    return (
      runsInfoData?.project?.runs?.edges?.reduce(
        (acc, edge) => {
          acc[edge.node.id] = edge.node?.runInfo;
          return acc;
        },
        {} as Partial<Record<string, RunInfoFragmentFragment['runInfo']>>
      ) ?? {}
    );
  }, [runsInfoData]);

  // Memoize individual run data processing
  const processedRunData = useMemo(() => {
    // Create a map of runset ID to query for quick lookup
    const runsetQueryMap = (pageQuery.runSets || []).reduce(
      (acc, query) => {
        acc[query.id] = query;
        return acc;
      },
      {} as Record<string, RunSetQuery>
    );

    return enabledKeys.reduce(
      (acc, key) => {
        const data = runDataByRunsetId[key];
        const runsetQuery = runsetQueryMap[key];

        // Get baseline/pinned IDs from this specific runset
        const baselineRunId = runsetQuery?.baselineRunId;
        const pinnedRunIds = runsetQuery?.pinnedRunIds || [];

        try {
          const runs = getOrderedRuns(
            data.data?.runsById ?? {},
            data.data?.order
          ).map(r => {
            const isBaseline = r.id === baselineRunId;
            const isPinned = pinnedRunIds.includes(r.id);

            return {
              ...r,
              runInfo: runInfoMap[r.id] ?? undefined,
              isBaseline,
              isPinned,
            };
          });
          acc[key] = runs;
        } catch (e) {
          console.error(e);
          acc[key] = [];
        }
        return acc;
      },
      {} as Record<string, BucketedLine[]>
    );
  }, [enabledKeys, runDataByRunsetId, runInfoMap, pageQuery.runSets]);

  // Create stable histories data reference
  const historyData = useMemo(() => {
    return enabledKeys.flatMap(key => processedRunData[key]);
  }, [enabledKeys, processedRunData]);

  const bucketedDataMemo = useMemo(() => {
    return {
      _dataType: POINT_VISUALIZATION_OPTIONS.BucketingGorilla,
      allRunsetsHaveBackendLines,
      backendLinesDomain,
      entityName: pageQuery.entityName,
      fetchedRunCount,
      fetchedRunCountByRunsetId,
      hasSubsampledGroups,
      histories: {data: historyData},
      initialLoading: false,
      isAggregated: aggregatedByRunsetId,
      projectName: pageQuery.projectName,
      totalGroupedIncludedRuns,
    };
  }, [
    aggregatedByRunsetId,
    allRunsetsHaveBackendLines,
    backendLinesDomain,
    fetchedRunCount,
    fetchedRunCountByRunsetId,
    hasSubsampledGroups,
    historyData,
    pageQuery.entityName,
    pageQuery.projectName,
    totalGroupedIncludedRuns,
  ]);

  const {error} = useMemo(() => {
    return {
      error:
        Object.values(runDataByRunsetId).find(v => v.error) ||
        runsInfoError?.networkError,
    };
  }, [runDataByRunsetId, runsInfoError]);

  if (error) {
    console.error(error);
  }

  /**
   * IMPORTANT!
   *
   *
   * Generates a cache key for bucketed data results.
   * This key is used to uniquely identify the data in the cache.
   * It should be updated whenever any of the dependencies that affect
   * the data query change.
   *
   * Do not change the cache key here unless you know what you are doing.
   * The cache key should be the panelQuery. If you need to update the cache key
   * to add any additional dependencies to trigger invalidation, add them to
   * the panelQuery.
   */

  const ultimateLoading = isLoading || runsInfoLoading;
  return useMemo(
    () => ({
      data: bucketedDataMemo,
      error,
      loading: ultimateLoading,
      key: panelQueryWithGroupKeys,
    }),
    [bucketedDataMemo, error, panelQueryWithGroupKeys, ultimateLoading]
  );
}
